/**
 * Trend detection utility.
 * Determines the current trend direction and color based on price history.
 */

import { DEFAULT_CHART_CONFIG } from '../config/constants';
import type { TrendDetectionConfig, ThemeConfig } from '../config/types';
import type { DataPoint } from '../types';

export type TrendDirection = 'bullish' | 'bearish' | 'neutral';

export interface TrendResult {
    direction: TrendDirection;
    color: string;
}

/**
 * Optional override thresholds from PriceConfig.
 * These take precedence over the trendConfig values when provided.
 */
export interface TrendThresholdOverrides {
    bullishThreshold?: number;
    bearishThreshold?: number;
}

/**
 * Binary search to find the first point where timestamp > cutoff.
 * Much faster than linear findIndex for large traces (O(log n) vs O(n)).
 *
 * @param trace - Sorted array of data points (by timestamp ascending)
 * @param cutoff - Timestamp cutoff value
 * @returns Index of first point with timestamp > cutoff, or -1 if none found
 */
const binarySearchLookback = (trace: DataPoint[], cutoff: number): number => {
    if (trace.length === 0) return -1;

    let low = 0;
    let high = trace.length - 1;

    // If all points are before the cutoff, return -1
    if (trace[high].timestamp <= cutoff) return -1;

    // If first point is already past cutoff, return 0
    if (trace[low].timestamp > cutoff) return 0;

    // Binary search for first element where timestamp > cutoff
    while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (trace[mid].timestamp <= cutoff) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    return low;
};

/**
 * Detects the current trend based on recent price movement.
 *
 * @param currentVal - Current price value
 * @param trace - Array of historical data points
 * @param currentTime - Current timestamp
 * @param trendConfig - Trend detection configuration
 * @param themeConfig - Theme configuration for colors
 * @param overrides - Optional price-scaled threshold overrides
 * @returns Trend direction and corresponding color
 */
export const detectTrend = (
    currentVal: number,
    trace: DataPoint[],
    currentTime: number,
    trendConfig: TrendDetectionConfig = DEFAULT_CHART_CONFIG.trendDetection,
    themeConfig: ThemeConfig = DEFAULT_CHART_CONFIG.theme,
    overrides?: TrendThresholdOverrides,
): TrendResult => {
    const { lookbackMs } = trendConfig;
    const { default: defaultColor, bullish, bearish } = themeConfig;

    // Use overrides if provided, otherwise use config values
    const bullishThreshold =
        overrides?.bullishThreshold ?? trendConfig.bullishThreshold;
    const bearishThreshold =
        overrides?.bearishThreshold ?? trendConfig.bearishThreshold;

    // Find the index of the first point within the lookback window
    // Using binary search (O(log n)) instead of findIndex (O(n))
    const lookbackIdx = binarySearchLookback(trace, currentTime - lookbackMs);

    if (lookbackIdx === -1 || lookbackIdx >= trace.length) {
        return { direction: 'neutral', color: defaultColor };
    }

    const diff = currentVal - trace[lookbackIdx].value;

    if (diff > bullishThreshold) {
        return { direction: 'bullish', color: bullish };
    } else if (diff < bearishThreshold) {
        return { direction: 'bearish', color: bearish };
    }

    return { direction: 'neutral', color: defaultColor };
};
